/**
 * RouteLayer displays the journey route when a vehicle is selected.
 *
 * Shows:
 * - Route polyline using the line's color
 * - Stop dots along the route
 */

import { useMemo, useEffect, useState, useCallback } from 'react';
import { Source, Layer, Popup, useMap } from 'react-map-gl/maplibre';
import type { LineLayerSpecification, CircleLayerSpecification, MapLayerMouseEvent } from 'maplibre-gl';
import type { FeatureCollection, LineString, Point } from 'geojson';

import { useVehicleStore } from '../../stores/vehicleStore';
import { getJourneyRoute } from '../../services/api';
import type { JourneyRouteResponse, RouteStop } from '../../types/api';

// Default color when line color is not available
const DEFAULT_ROUTE_COLOR = '#1976D2';

// Layer IDs
const ROUTE_LINE_SOURCE = 'route-line-source';
const ROUTE_STOPS_SOURCE = 'route-stops-source';
const ROUTE_LINE_LAYER = 'route-line-layer';
const ROUTE_STOPS_LAYER = 'route-stops-layer';

interface RouteLayerState {
  route: JourneyRouteResponse | null;
  loading: boolean;
  error: string | null;
}

interface HoveredStop {
  name: string;
  longitude: number;
  latitude: number;
  pinned: boolean; // true if clicked (persists until click elsewhere)
}

/**
 * Create GeoJSON for route polyline.
 * Converts from [lat, lon] to [lon, lat] for GeoJSON.
 */
function createLineGeoJSON(
  polyline: [number, number][] | undefined
): FeatureCollection<LineString> {
  if (!polyline || polyline.length < 2) {
    return { type: 'FeatureCollection', features: [] };
  }

  // Convert from [lat, lon] to [lon, lat] for GeoJSON
  const coords: [number, number][] = polyline.map(([lat, lon]) => [lon, lat]);

  return {
    type: 'FeatureCollection',
    features: [
      {
        type: 'Feature',
        geometry: {
          type: 'LineString',
          coordinates: coords,
        },
        properties: {},
      },
    ],
  };
}

/**
 * Create GeoJSON for stop dots.
 */
function createStopsGeoJSON(
  stops: RouteStop[] | undefined
): FeatureCollection<Point> {
  if (!stops || stops.length === 0) {
    return { type: 'FeatureCollection', features: [] };
  }

  return {
    type: 'FeatureCollection',
    features: stops.map((stop, index) => ({
      type: 'Feature' as const,
      geometry: {
        type: 'Point' as const,
        coordinates: [stop.longitude, stop.latitude],
      },
      properties: {
        name: stop.name,
        index,
      },
    })),
  };
}

export function RouteLayer() {
  const selectedVehicleId = useVehicleStore((state) => state.selectedVehicleId);
  const vehicles = useVehicleStore((state) => state.vehicles);

  const [state, setState] = useState<RouteLayerState>({
    route: null,
    loading: false,
    error: null,
  });

  // Tooltip state for hovered stop
  const { current: map } = useMap();
  const [hoveredStop, setHoveredStop] = useState<HoveredStop | null>(null);

  // Get the selected vehicle's journey_gid
  const journeyGid = useMemo(() => {
    if (!selectedVehicleId) return null;
    const vehicle = vehicles.get(selectedVehicleId);
    return vehicle?.journey_gid ?? null;
  }, [selectedVehicleId, vehicles]);

  // Fetch route when vehicle is selected
  useEffect(() => {
    // Early return if no vehicle selected
    if (!selectedVehicleId || !journeyGid) {
      return;
    }

    // Create abort controller for this request
    const controller = new AbortController();
    let isMounted = true;

    // Async IIFE to handle loading state transitions
    (async () => {
      // Set loading state at start of async operation
      if (isMounted) {
        setState({ route: null, loading: true, error: null });
      }

      try {
        const route = await getJourneyRoute(journeyGid);
        if (isMounted && !controller.signal.aborted) {
          setState({ route, loading: false, error: null });
        }
      } catch (err) {
        if (isMounted && !controller.signal.aborted) {
          const message = err instanceof Error ? err.message : 'Failed to load route';
          // 404/410 means route not available - not really an error
          if (message.includes('404') || message.includes('410')) {
            setState({ route: null, loading: false, error: null });
          } else {
            setState({ route: null, loading: false, error: message });
          }
        }
      }
    })();

    // Cleanup: abort request and clear state when vehicle changes
    return () => {
      isMounted = false;
      controller.abort();
      setState({ route: null, loading: false, error: null });
    };
  }, [selectedVehicleId, journeyGid]);

  // Compute GeoJSON data
  const lineGeoJSON = useMemo(() => {
    return createLineGeoJSON(state.route?.polyline);
  }, [state.route?.polyline]);

  const stopsGeoJSON = useMemo(() => {
    return createStopsGeoJSON(state.route?.stops);
  }, [state.route?.stops]);

  // Use route's line_color, with fallback to default (no flash since we don't render until route exists)
  const routeColor = state.route?.line_color ?? DEFAULT_ROUTE_COLOR;

  // Layer styles
  const lineStyle: LineLayerSpecification = useMemo(
    () => ({
      id: ROUTE_LINE_LAYER,
      type: 'line',
      source: ROUTE_LINE_SOURCE,
      paint: {
        'line-color': routeColor,
        'line-width': 4,
        'line-opacity': 0.8,
      },
      layout: {
        'line-cap': 'round',
        'line-join': 'round',
      },
    }),
    [routeColor]
  );

  const stopsStyle: CircleLayerSpecification = useMemo(
    () => ({
      id: ROUTE_STOPS_LAYER,
      type: 'circle',
      source: ROUTE_STOPS_SOURCE,
      paint: {
        'circle-radius': 5,
        'circle-color': '#ffffff',
        'circle-stroke-color': routeColor,
        'circle-stroke-width': 2,
      },
    }),
    [routeColor]
  );

  // Event handlers for stop tooltips
  const handleStopMouseEnter = useCallback(
    (e: MapLayerMouseEvent) => {
      const feature = e.features?.[0];
      if (!feature?.properties?.name || !map) return;

      map.getCanvas().style.cursor = 'pointer';

      const [longitude, latitude] = (feature.geometry as Point).coordinates as [number, number];

      setHoveredStop((prev) => {
        // Don't override a pinned tooltip with hover
        if (prev?.pinned) return prev;
        return {
          name: (feature.properties.name as string).replace(/, Göteborg$/, ''),
          longitude,
          latitude,
          pinned: false,
        };
      });
    },
    [map]
  );

  const handleStopMouseLeave = useCallback(() => {
    if (map) map.getCanvas().style.cursor = '';
    // Only clear if not pinned by click
    setHoveredStop((prev) => (prev?.pinned ? prev : null));
  }, [map]);

  const handleStopClick = useCallback(
    (e: MapLayerMouseEvent) => {
      const feature = e.features?.[0];
      if (!feature?.properties?.name || !map) return;

      const [longitude, latitude] = (feature.geometry as Point).coordinates as [number, number];
      const name = (feature.properties.name as string).replace(/, Göteborg$/, '');
      setHoveredStop({ name, longitude, latitude, pinned: true });
    },
    [map]
  );

  // Dismiss tooltip when clicking elsewhere on map (not on a stop)
  const handleMapClick = useCallback(
    (e: MapLayerMouseEvent) => {
      if (!map) return;

      // Check if click was on a stop - if so, don't dismiss
      const features = map.queryRenderedFeatures(e.point, {
        layers: [ROUTE_STOPS_LAYER],
      });

      if (features.length === 0) {
        // Clicked elsewhere, dismiss tooltip
        setHoveredStop(null);
      }
    },
    [map]
  );

  // Register map event listeners for stop tooltips
  useEffect(() => {
    if (!map || !state.route) return;

    map.on('mouseenter', ROUTE_STOPS_LAYER, handleStopMouseEnter);
    map.on('mouseleave', ROUTE_STOPS_LAYER, handleStopMouseLeave);
    map.on('click', ROUTE_STOPS_LAYER, handleStopClick);
    map.on('click', handleMapClick); // Map-level click to dismiss

    return () => {
      map.off('mouseenter', ROUTE_STOPS_LAYER, handleStopMouseEnter);
      map.off('mouseleave', ROUTE_STOPS_LAYER, handleStopMouseLeave);
      map.off('click', ROUTE_STOPS_LAYER, handleStopClick);
      map.off('click', handleMapClick);
      setHoveredStop(null); // Clear tooltip on cleanup
    };
  }, [map, state.route, handleStopMouseEnter, handleStopMouseLeave, handleStopClick, handleMapClick]);

  // Show loading indicator (floating, doesn't affect Sources/Layers)
  const loadingIndicator = state.loading ? (
    <div className="absolute bottom-5 left-5 bg-white/90 px-3 py-2 rounded text-xs z-50">
      Loading route...
    </div>
  ) : null;

  return (
    <>
      {loadingIndicator}

      {/* Only render route layers when route data exists (prevents color flash) */}
      {state.route && (
        <>
          <Source id={ROUTE_LINE_SOURCE} type="geojson" data={lineGeoJSON}>
            <Layer {...lineStyle} />
          </Source>
          <Source id={ROUTE_STOPS_SOURCE} type="geojson" data={stopsGeoJSON}>
            <Layer {...stopsStyle} />
          </Source>
        </>
      )}

      {/* Stop name tooltip */}
      {hoveredStop && (
        <Popup
          longitude={hoveredStop.longitude}
          latitude={hoveredStop.latitude}
          anchor="bottom"
          closeButton={false}
          closeOnClick={false}
          className="stop-tooltip"
          offset={8}
        >
          <span className="text-sm text-gray-900 dark:text-gray-100 whitespace-nowrap">
            {hoveredStop.name}
          </span>
        </Popup>
      )}
    </>
  );
}
